import React, { memo, useMemo } from 'react';
import { Button, Col, Descriptions, Divider, List, Modal, Row, Space, Table, Timeline, Typography } from 'antd';
import dayjs from 'dayjs';
import { nanoid } from 'nanoid';
import { useFetchPayoutsLedgerQuery } from '@/graphql/generated/graphql';
import { Loader } from '@/components';
import { removeJobIdPrefix } from '@/utils';
import styles from './styles.module.pcss';

type Props = {
  isModalVisible: boolean;
  toggleModal: (visible: boolean, componentId?: string) => void;
  eventId: string;
  componentId: string;
};

const LedgerModal = memo<Props>(({ toggleModal, isModalVisible, eventId, componentId }) => {
  const { data, loading } = useFetchPayoutsLedgerQuery({
    variables: {
      eventId,
      componentIds: [componentId],
    },
  });

  const eventResult = useMemo<Record<string, any> | undefined>(() => {
    if (data?.payoutsLedger) {
      return data?.payoutsLedger[0];
    }
  }, [data?.payoutsLedger]);

  const renderMain = () => {
    if (loading) {
      return <Loader />;
    }

    const getAllIndividualTiers = () => {
      const parsedTiers = eventResult?.result?.then?.tiers;
      const rawTiers = eventResult?.payload?.rawComponent?.then?.tiers;
      const remainingTiers = rawTiers?.slice(parsedTiers?.length, rawTiers.length);
      return [
        ...parsedTiers,
        ...remainingTiers.map((tier: any) => ({
          condition: {
            ...tier?.condition,
            isPassed: undefined,
            actualValue: 'N/A',
            parsedComparisonValue:
              tier?.condition?.comparisonValue !== undefined ? tier?.condition?.comparisonValue : 'N/A',
          },
          payout: 'N/A',
        })),
      ];
    };

    return (
      <div>
        <Descriptions
          title={<Typography.Title level={3}>Event Payload</Typography.Title>}
          bordered
          column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
        >
          <Descriptions.Item label="User ID">
            {eventResult?.user?.identifier} ({eventResult?.user?.id})
          </Descriptions.Item>
          <Descriptions.Item label="Job ID">{eventResult?.payload?.rawEvent?.jobId}</Descriptions.Item>
          <Descriptions.Item label="Component">
            {eventResult?.payload?.rawComponent?.componentName || eventResult?.payload?.rawComponent?.componentId}
          </Descriptions.Item>
          <Descriptions.Item label="Event ID">{eventResult?.payload?.rawEvent?.id}</Descriptions.Item>
          <Descriptions.Item label="Result / Payout At">
            {[
              {
                key: 'Result / Payout Calculated',
                value: `${dayjs(eventResult?.result?.upserted_at)?.format('MM-DD-YYYY HH:mm:ss.SSS')}`,
              },
              {
                key: 'Dispatched',
                value: `${dayjs(eventResult?.dispatched_at)?.format('MM-DD-YYYY HH:mm:ss.SSS')}`,
              },
              {
                key: 'Event Updated',
                value: `${dayjs(eventResult?.event_upserted_at)?.format('MM-DD-YYYY HH:mm:ss.SSS')}`,
              },
              {
                key: 'Event Happened',
                value: `${dayjs(eventResult?.payload?.rawEvent?.ts)?.format(
                  'MM-DD-YYYY HH:mm:ss.SSS'
                )} (Arya's internal sequence: ${eventResult?.payload?.rawEvent?.seq})`,
              },
            ].map((cp, index) => (
              <Timeline.Item key={index}>
                {cp.key} {cp.value}
              </Timeline.Item>
            ))}
          </Descriptions.Item>
          <Descriptions.Item label="Event Result Parameters">
            <List
              size="small"
              dataSource={eventResult?.result?.params}
              renderItem={(item: any) => (
                <>
                  <Typography.Paragraph strong>
                    {item?.key}: {item?.value}
                  </Typography.Paragraph>
                  {item?.valueFormula ? (
                    <Space direction="vertical" size={[0, 'small']}>
                      <Typography.Paragraph>
                        Formula: <Typography.Text code>{item?.valueFormula}</Typography.Text>
                      </Typography.Paragraph>
                      <Typography.Paragraph>
                        Script Parsing Order: {item?.formulaScriptParsingOrder}
                      </Typography.Paragraph>
                    </Space>
                  ) : null}
                  <Divider />
                </>
              )}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Raw Event Data">
            <Table
              columns={[
                {
                  title: 'Key',
                  dataIndex: 'paramKey',
                  key: 'paramKey',
                  width: '50%',
                },
                {
                  title: 'Value',
                  dataIndex: 'value',
                  key: 'value',
                  width: '40%',
                },
                {
                  title: 'Is User Attribute?',
                  dataIndex: 'attribute',
                  key: 'attribute',
                  render: (isUserAttribute) => <Typography.Text>{isUserAttribute ? 'YES' : ''}</Typography.Text>,
                  width: '10%',
                },
              ]}
              dataSource={eventResult?.payload?.rawEvent?.data?.map((p: any) => {
                return {
                  paramKey: p.key,
                  value: p.value,
                  attribute: p.isUserAttribute,
                };
              })}
              size="small"
            />
          </Descriptions.Item>
        </Descriptions>

        <Typography.Title level={3} style={{ marginTop: '40px' }}>
          Did this event happen within the component period?
          <Typography.Text strong>{eventResult?.result?.isWithinPlanPeriod ? ' YES' : ' NO'}</Typography.Text>
        </Typography.Title>

        <Typography.Paragraph>
          <Typography.Text strong>Component Period:</Typography.Text>&nbsp;
          {dayjs(eventResult?.payload?.rawComponent?.startTs)?.format('MM-DD-YYYY HH:mm:ss.SSS')} -{' '}
          {eventResult?.payload?.rawComponent?.endTs
            ? dayjs(eventResult?.payload?.rawComponent?.endTs)?.format('MM-DD-YYYY HH:mm:ss.SSS')
            : 'Indefinite'}
        </Typography.Paragraph>

        <Typography.Paragraph>
          <Typography.Text strong>Event Timestamp:</Typography.Text>{' '}
          {dayjs(eventResult?.payload?.rawEvent?.ts).format('MM-DD-YYYY HH:mm:ss.SSS')}
        </Typography.Paragraph>

        {eventResult?.result?.isWithinPlanPeriod ? (
          <div>
            <Typography.Title level={3} style={{ marginTop: '40px' }}>
              Was this event eligible for the component?
              <Typography.Text strong>{eventResult?.result?.isEligible ? ' YES' : ' NO'}</Typography.Text>
            </Typography.Title>

            <Typography.Paragraph>
              A user is eligible for payout on a component if they pass the conditions of{' '}
              <Typography.Text strong>one or more</Typography.Text> eligibility groups.
            </Typography.Paragraph>

            <Typography.Paragraph>
              At an eligibility group level, a user can pass the eligibility group if{' '}
              <Typography.Text strong>either</Typography.Text> of the following are true:
              <ul>
                <li>The user appears in the static list of eligible users;</li>
                <li>The user passes one or all (depending on the group setup) conditions within the group.</li>
              </ul>
            </Typography.Paragraph>

            {eventResult?.result?.eligibilityGroups?.map((eg: any, i: number) => (
              <div key={i}>
                <Typography.Title level={4}>Eligibility Group #{i + 1}</Typography.Title>

                <Typography.Title level={5}>Static User List</Typography.Title>

                {eg?.eligibleUsers && eg?.eligibleUsers?.length > 0 ? (
                  <>
                    This user{' '}
                    {eg?.isInEligibleUsersList ? (
                      <Typography.Text strong>IS</Typography.Text>
                    ) : (
                      <Typography.Text strong>IS NOT</Typography.Text>
                    )}{' '}
                    in the list of eligible users.
                  </>
                ) : (
                  'This eligibility group has no static user list'
                )}

                <Typography.Title level={5}>Conditions</Typography.Title>

                <Table
                  pagination={false}
                  columns={
                    [
                      {
                        title: `${eg?.allEligibilityConditionsRequired ? 'All' : 'Any'} conditions must be met`,
                        dataIndex: 'paramKey',
                        key: 'paramKey',
                        render: (_: any, { aggregate, paramKey, period, comparator, parsedComparisonValue }: any) => (
                          <span>
                            {aggregate ? ` ${aggregate} of ` : null}
                            {paramKey}
                            {period ? ` over ${period} ` : null}
                            {comparator}
                            {parsedComparisonValue}
                          </span>
                        ),
                      },
                      {
                        title: 'Actual Value',
                        dataIndex: 'actualValue',
                        key: 'actualValue',
                      },
                      {
                        title: 'Is Passed?',
                        dataIndex: 'isPassed',
                        key: 'isPassed',
                        render: (_: any, { isPassed }: any) => (isPassed ? 'PASSED' : 'FAILED'),
                      },
                    ] as any
                  }
                  dataSource={eg?.eligibility?.map((e: any) => ({
                    ...e,
                    key: nanoid(),
                    paramKey: e?.key,
                  }))}
                />
              </div>
            ))}

            <Typography.Title level={3} style={{ marginTop: '40px' }}>
              Was the trigger condition met?
              <Typography.Text strong>{eventResult?.result?.whenPassed ? ' YES' : ' NO'}</Typography.Text>
            </Typography.Title>

            <Typography.Paragraph>
              {eventResult?.result?.when?.key} {eventResult?.result?.when?.comparator}{' '}
              {eventResult?.result?.when?.comparisonValue}
            </Typography.Paragraph>

            <Typography.Paragraph>
              <Typography.Text strong>Event Value:</Typography.Text> {eventResult?.result?.when?.actualValue}
            </Typography.Paragraph>

            {eventResult?.result?.whenPassed ? (
              <div>
                <Typography.Title level={3} style={{ marginTop: '40px' }}>
                  Were the conditions required to generate a payout met?
                  <Typography.Text strong>{eventResult?.result?.ifPassed ? ' YES' : ' NO'}</Typography.Text>
                </Typography.Title>

                <Table
                  pagination={false}
                  columns={
                    [
                      {
                        title: `${
                          eventResult?.payload?.rawComponent?.allIfConditionsRequired ? 'All' : 'Any'
                        } conditions must be met`,
                        dataIndex: 'paramKey',
                        key: 'paramKey',
                        render: (_: any, { aggregate, paramKey, period, comparator, parsedComparisonValue }: any) => (
                          <span>
                            {aggregate ? ` ${aggregate} of ` : null}
                            {paramKey}
                            {period ? ` over ${period} ` : null}
                            {comparator}
                            {parsedComparisonValue}
                          </span>
                        ),
                      },
                      {
                        title: 'Actual Value',
                        dataIndex: 'actualValue',
                        key: 'actualValue',
                      },
                      {
                        title: 'Is Passed?',
                        dataIndex: 'isPassed',
                        key: 'isPassed',
                        render: (_: any, { isPassed }: any) => (isPassed ? 'PASSED' : 'FAILED'),
                      },
                    ] as any
                  }
                  dataSource={eventResult?.result?.if?.map((con: any) => ({
                    ...con,
                    key: nanoid(),
                    paramKey: con?.key,
                  }))}
                />

                {eventResult?.result?.ifPassed ? (
                  <div>
                    <Typography.Title level={3} style={{ marginTop: '40px' }}>
                      Payout Calculations
                    </Typography.Title>

                    {eventResult?.result?.then?.type === 'plain' ? (
                      <div>
                        <Typography.Text strong>Payout:</Typography.Text> {eventResult?.result?.then?.payoutValue}
                      </div>
                    ) : eventResult?.result?.then?.type === 'individual' ? (
                      <div>
                        <Typography.Paragraph>
                          This component pays out on the highest-order tier that is passed
                        </Typography.Paragraph>

                        <Table
                          pagination={false}
                          columns={
                            [
                              {
                                title: 'Order',
                                dataIndex: 'tierIndex',
                                key: 'tierIndex',
                              },
                              {
                                title: 'Tier Condition',
                                dataIndex: 'condition',
                                key: 'condition',
                                render: (_: any, { condition }: any) => (
                                  <span>
                                    {condition?.aggregate ? ` ${condition?.aggregate} of ` : null}
                                    {condition?.paramKey}
                                    {condition?.period ? ` over ${condition?.period} ` : null}
                                    {condition?.comparator}
                                    {condition?.parsedComparisonValue}
                                  </span>
                                ),
                              },
                              {
                                title: 'Actual Value',
                                dataIndex: 'actualValue',
                                key: 'actualValue',
                                render: (_: any, { condition: { actualValue } }: any) => (
                                  <span>{actualValue || 0}</span>
                                ),
                              },
                              {
                                title: 'Passed?',
                                dataIndex: 'passed',
                                key: 'passed',
                                render: (_: any, { condition: { isPassed } }: any) =>
                                  isPassed === true ? 'PASSED' : isPassed === false ? 'FAILED' : 'N/A',
                              },
                              {
                                title: 'Payout',
                                dataIndex: 'payout',
                                key: 'payout',
                                render: (_: any, { payoutValue }: any) => payoutValue || 0,
                              },
                            ] as any
                          }
                          dataSource={getAllIndividualTiers()?.map((tier: any, i: number) => ({
                            key: nanoid(),
                            tierIndex: i + 1,
                            ...tier,
                          }))}
                        />
                      </div>
                    ) : eventResult?.result?.then?.type === 'incremental' ? (
                      <div>
                        <Typography.Paragraph>
                          The commission rates are based on &nbsp;
                          {eventResult?.result?.then?.incrementalAggregateParameter?.aggregate}
                          &nbsp;of {eventResult?.result?.then?.incrementalAggregateParameter?.key}
                          &nbsp;over {eventResult?.result?.then?.incrementalAggregateParameter?.period}
                          &nbsp;(referred to as &quot;Tier Condition&quot; below)
                        </Typography.Paragraph>

                        <Typography.Paragraph>
                          <strong>Value Before Event:</strong>{' '}
                          {eventResult?.result?.then?.incrementalAggregateParameter?.aggregateValue -
                            eventResult?.result?.then?.incrementalAggregateParameter?.eventValue}
                        </Typography.Paragraph>

                        <Typography.Paragraph>
                          <strong>Value of Event:</strong>{' '}
                          {eventResult?.result?.then?.incrementalAggregateParameter?.eventValue}
                        </Typography.Paragraph>

                        <Typography.Paragraph>
                          <strong>Value After Event:</strong>{' '}
                          {eventResult?.result?.then?.incrementalAggregateParameter?.aggregateValue}
                        </Typography.Paragraph>

                        <Table
                          pagination={false}
                          columns={[
                            {
                              title: 'Tier',
                              dataIndex: 'tierIndex',
                              key: 'tierIndex',
                            },
                            {
                              title: 'Tier Condition Value',
                              dataIndex: 'threshold',
                              key: 'threshold',
                              render: (_: any, { tier }: any) => <span>Up to {tier?.threshold.value}</span>,
                            },
                            {
                              title: 'Amount Within Tier',
                              dataIndex: 'actualValue',
                              key: 'actualValue',
                              render: (_: any, { tier }: any) => <span>{tier?.amountWithinThisTier || 0}</span>,
                            },
                            {
                              title: 'Payout',
                              dataIndex: 'payout',
                              key: 'payout',
                              render: (_: any, { tier }: any) =>
                                tier?.commissionParameter ? (
                                  <span>
                                    {tier?.commissionParameter?.map((p: any) => `${p}%`)?.join(' * ')} of{' '}
                                    {tier?.amountWithinThisTier} = {tier?.paid}
                                  </span>
                                ) : (
                                  <span>
                                    {tier?.parsedCommission * 100}% of {tier?.amountWithinThisTier} = {tier?.payout}
                                  </span>
                                ),
                            },
                          ]}
                          dataSource={eventResult?.result?.then?.tiers?.map((tier: any, i: number) => ({
                            key: nanoid(),
                            tierIndex: i + 1,
                            tier,
                          }))}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <Modal
      centered
      destroyOnClose
      closable={false}
      bodyStyle={{ height: 'calc(80vh - 30px)', overflow: 'auto', padding: '20px' }}
      width={'calc(80vw - 30px)'}
      open={isModalVisible}
      footer={null}
      onCancel={() => toggleModal(false, undefined)}
      title={
        loading ? (
          <Loader />
        ) : (
          <Row justify="space-between" className={styles.header}>
            <Col>
              <Typography.Title level={3} className={styles.title}>
                {removeJobIdPrefix(eventResult?.payload?.rawEvent?.jobId)}
              </Typography.Title>
            </Col>
            <Col>
              <Button type="primary" className={styles.closeBtn} onClick={() => toggleModal(false)}>
                CLOSE
              </Button>
            </Col>
          </Row>
        )
      }
      className={styles.root}
    >
      {renderMain()}
    </Modal>
  );
});

export default LedgerModal;
